/**
 * @file src/App.tsx
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import Admin from "./pages/Admin";
import KneAdmin from "./pages/KneAdmin";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./context/auth";
import AdminLoginPage from "./pages/AdminLogin";
import HomePage from "./pages/Home";
import PrivacyPage from "./pages/Privacy";
import SupportPage from "./pages/Support";
import TermsPage from "./pages/Terms";
import { setLocale as persistLocale } from "./i18n";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isRestoring } = useAuth();
  if (isRestoring) {
    return <div role="status" aria-live="polite">Restoring session…</div>;
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin-login" replace />;
}

function RetiredRoute() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en" : "zh";
  const copy = {
    zh: {
      eyebrow: "Spark KNE",
      title: "原 Spark Web 服务已停止",
      body: "旧版 Chat、注册和登录入口已经关闭。Spark 当前主线是 AI 知识可信验证插件，用于在 ChatGPT、千问等平台中辅助判断 AI 回答的知识可靠性。",
      action: "查看新的 Spark KNE",
      switchLabel: "EN",
      langLabel: "切换为英文",
    },
    en: {
      eyebrow: "Spark KNE",
      title: "The original Spark Web service has been retired",
      body: "The old Chat, registration, and login entries are closed. Spark now focuses on the AI knowledge verification plugin, helping users judge the reliability of AI answers on ChatGPT, Qwen, and other platforms.",
      action: "View the new Spark KNE",
      switchLabel: "中文",
      langLabel: "Switch to Chinese",
    },
  }[locale];

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#07130d",
        color: "#e8f6eb",
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <section style={{ width: "min(620px, 100%)", lineHeight: 1.65 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 12,
          }}
        >
          <div style={{ color: "#58d16c", fontSize: 13, fontWeight: 700 }}>
            {copy.eyebrow}
          </div>
          <button
            type="button"
            aria-label={copy.langLabel}
            onClick={() => persistLocale(locale === "en" ? "zh" : "en")}
            style={{
              border: "1px solid rgba(88, 209, 108, .35)",
              background: "rgba(88, 209, 108, .08)",
              color: "#58d16c",
              borderRadius: 8,
              padding: "7px 10px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {copy.switchLabel}
          </button>
        </div>
        <h1 style={{ margin: "0 0 14px", fontSize: "clamp(26px, 5vw, 38px)", lineHeight: 1.15 }}>
          {copy.title}
        </h1>
        <p style={{ margin: "0 0 22px", color: "#a9b9ad", fontSize: 16 }}>
          {copy.body}
        </p>
        <a
          href="/"
          style={{
            display: "inline-flex",
            color: "#07130d",
            background: "#58d16c",
            padding: "10px 14px",
            borderRadius: 8,
            textDecoration: "none",
            fontWeight: 700,
          }}
        >
          {copy.action}
        </a>
      </section>
    </main>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/login" element={<RetiredRoute />} />
          <Route path="/register" element={<RetiredRoute />} />
          <Route path="/chat" element={<RetiredRoute />} />
          <Route path="/settings" element={<RetiredRoute />} />
          <Route path="/understanding" element={<RetiredRoute />} />
          <Route path="/admin-login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kneadmin"
            element={
              <ProtectedRoute>
                <KneAdmin />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
